import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { getFromDB } from "../../component/indexedDB/indexedDB"; // Adjust the import path as necessary
import QuestionPublicLink from "../../component/PublicLink/PublicLink";
import { QuizButton } from "../../types/GeneralStyles";
import {
  ResultScore,
  ResultScreen,
  QuizResultDiv,
  QuizResultFlexContainer,
  QuizResultDescription,
  SimpleResultQuizResultScore,
  QuizEndResultText,
  SimpleResultWebpageItem,
  SimpleResultQuizResultScoreSub,
} from "../Webpage.components";

// Helper function to generate a unique ID using the crypto API (without hyphens)
const generateUniqueId = () => {
  if (crypto && crypto.getRandomValues) {
    // Generate 16 random bytes
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);

    // Convert bytes to a Base64-like string without special characters
    const uniqueId = Array.from(array, (byte) =>
      ("0" + byte.toString(16)).slice(-2)
    ).join("");
    // console.log("Generated Unique ID (crypto):", uniqueId);
    return uniqueId;
  } else {
    // Fallback to the pure random generator if crypto is unavailable
    return generatePureRandomId();
  }
};

// Fallback pure random ID generator
const generatePureRandomId = (length = 16) => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let uniqueId = "";

  for (let i = 0; i < length; i++) {
    uniqueId += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  // console.log("Generated Unique ID (fallback):", uniqueId);
  return uniqueId;
};

const SendToAPI = ({
  result, // This should be data.attributes.toolData.quiz_results[0]
  oriQuiz, // This contains the entire quiz data from 'data'
  overViewPage,
  enableNavigationButton,
  resetQuiz,
}) => {
  const [answers, setAnswers] = useState({});
  const [attempts, setAttempts] = useState({});
  const [dbName, setDbName] = useState("");
  const [sortedQuestions, setSortedQuestions] = useState([]);
  const [apiResponse, setApiResponse] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [hasSent, setHasSent] = useState(false);

  // Fetch answers and attempts from IndexedDB when the component mounts
  useEffect(() => {
    // console.log("Component mounted: Fetching answers and attempts.");
    enableNavigationButton();
    const fetchAnswersAndAttempts = async () => {
      const currentDbName =
        sessionStorage.getItem("currentSessionName") || "Leerling";
      // console.log("Current DB Name:", currentDbName);
      const data = await getFromDB(currentDbName);
      // console.log("Fetched from IndexedDB:", data);

      // console.log("Data fetched from IndexedDB:", data);

      const answersMap = data.reduce((acc, item) => {
        if (Array.isArray(item.answer)) {
          // Lookup original question
          const question = oriQuiz?.Quizes.find(
            (q) => q.quiz_item_id === item.id
          );
          if (question?.__component === "quiz-game-types.stelling") {
            acc[item.id] = item.answer
              .map((id) => {
                const found = question.Antwoord_items.find(
                  (ai) => ai.id === parseInt(id)
                );
                return found?.content || "";
              })
              .join(",");
          } else if (question?.__component === "quiz-game-types.sorteer") {
            acc[item.id] = item.answer
              .map((item) => item?.content || item)
              .join(",");
          } else {
            acc[item.id] = item.answer.join(",");
          }
        } else {
          acc[item.id] = item.answer;
        }
        return acc;
      }, {});
      // console.log("Mapped Answers:", answersMap);
      setAnswers(answersMap);
      // console.log("Mapped Answers:", answersMap);

      const attemptsMap = data.reduce((acc, item) => {
        acc[item.id] = item.attempts;
        return acc;
      }, {});
      // console.log("Mapped Attempts:", attemptsMap);
      setAttempts(attemptsMap);
    };
    fetchAnswersAndAttempts();
  }, [enableNavigationButton]);

  // Sort questions based on quiz_shape_order from oriQuiz
  useEffect(() => {
    // console.log("Sorting questions based on quiz_shape_order.");
    // Access quiz_shape_order from oriQuiz
    const quizShape = oriQuiz?.quiz_shape;
    if (!quizShape || quizShape.length === 0) {
      console.warn("No quiz_shape found in oriQuiz.");
      return;
    }

    const quizShapeOrder = quizShape[0].quiz_shape_order_combined;
    // console.log(quizShapeOrder);

    if (!quizShapeOrder || quizShapeOrder.length === 0) {
      console.warn("No quiz_shape_order found in oriQuiz.");
      return;
    }

    // console.log("Quiz Shape Order:", quizShapeOrder);

    const sorted = quizShapeOrder
      .map((orderItem) => {
        const question = oriQuiz?.Quizes.find(
          (q) => q.quiz_item_id === orderItem.quiz_item_id
        );
        if (question) {
          // console.log(`Found question for quiz_item_id '${orderItem.quiz_item_id}':`, question);
        } else {
          console.warn(
            `No question found for quiz_item_id '${orderItem.quiz_item_id}'`
          );
        }
        return question;
      })
      .filter((q) => q !== undefined); // Remove undefined questions

    // console.log("Sorted Questions:", sorted);
    setSortedQuestions(sorted);

    const fetchedDbName =
      sessionStorage.getItem("currentSessionName") || "Leerling";
    // console.log("Setting DB Name to:", fetchedDbName);
    setDbName(fetchedDbName);
  }, [oriQuiz]);

  // Helper function to check if all dynamic fields have answers
  const allDynamicFieldsAnswered = () => {
    const apifields = result?.apifields || [];
    const dynamicFields = apifields.filter(
      (field) => field.prefilled_or_dynamic === "dynamic"
    );
    // console.log("Dynamic Fields:", dynamicFields);
    const allAnswered = dynamicFields.every((field) => {
      const answer = answers[field.field_value];
      const isAnswered =
        answer !== undefined && answer !== null && answer !== "";
      // console.log(`Field '${field.field_name}' (dynamic):`, isAnswered ? "Answered" : "Not Answered");
      return isAnswered;
    });
    // console.log("All dynamic fields answered:", allAnswered);
    return allAnswered;
  };

  // Send API request when all required data is available
  useEffect(() => {
    if (hasSent) {
      // console.log("API request already sent. Skipping.");
      return; // Prevent multiple sends
    }
    // Replace placeholders like ${key} in a JSON object recursively
    function replacePlaceholders(obj, answers) {
      if (typeof obj === "string") {
        return obj.replace(/\$\{(.*?)\}/g, (_, key) => {
  // console.log("Replacing placeholder:", key, "→", answers[key]);
  return answers[key] !== undefined ? answers[key] : "";
});
      }

      if (Array.isArray(obj)) {
        return obj.map((item) => replacePlaceholders(item, answers));
      }

      if (typeof obj === "object" && obj !== null) {
        const newObj = {};
        for (const key in obj) {
          newObj[key] = replacePlaceholders(obj[key], answers);
        }
        return newObj;
      }

      return obj; // Leave other types (number, boolean, null) unchanged
    }
    const sendApiRequest = async () => {
      if (hasSent) return;

  // 💡 This prevents sending if answers are still empty
  if (!answers || Object.keys(answers).length === 0) {
    console.warn("Answers not loaded yet — skipping API call.");
    return;
  }


      if (overViewPage) {
        return true;
      }
      // console.log("Preparing to send API request.");
      const { url, method, bodyparameters, apifields } = result;
      // console.log("API URL:", url);
      // console.log("API Method:", method);
      // console.log("API Body Parameters:", bodyparameters);
      // console.log("API Fields:", apifields);
      // console.log("xkey:", process.env.X_PROJECT_KEY_TC);

      const payload = {};
      apifields.forEach((field) => {
  const {
    field_name,
    field_value,
    prefilled_or_dynamic,
    field_type,
  } = field;

  // Response ID
  if (prefilled_or_dynamic === "response_id") {
    payload[field_name] = generateUniqueId();
    return;
  }

  // Prefilled fields
  if (prefilled_or_dynamic === "prefilled") {
    payload[field_name] = field_value;
    return;
  }

  // JSON dynamic field
  if (prefilled_or_dynamic === "dynamic" && field_type === "JSON") {
    try {
      const parsedJSON = JSON.parse(field_value);
      const replaced = replacePlaceholders(parsedJSON, answers);
      payload[field_name] = replaced;
      // console.log(`Parsed JSON Field - ${field_name}:`, replaced);
    } catch (e) {
      // console.error("Invalid JSON in field_value:", field_value);
      payload[field_name] = {};
    }
    return; // 🔒 Don't let other logic override this
  }

  // Other dynamic types
  if (prefilled_or_dynamic === "dynamic") {
    const answer = answers[field_value];
    let valueToSend = answer || null;

    if (Array.isArray(answer)) {
      const question = sortedQuestions.find(
        (q) => q.quiz_item_id === field_value
      );
      if (question) {
        if (question.__component === "quiz-game-types.stelling") {
          valueToSend = answer
            .map((id) => {
              const answerItem = question.Antwoord_items.find(
                (item) => item.id === parseInt(id)
              );
              return answerItem ? answerItem.content : "";
            })
            .join(",");
        } else if (question.__component === "quiz-game-types.sorteer") {
          if (
            answer.length > 0 &&
            typeof answer[0] === "object" &&
            answer[0].content
          ) {
            valueToSend = answer.map((item) => item.content).join(",");
          } else {
            valueToSend = answer.join(",");
          }
        } else {
          valueToSend = answer.join(",");
        }
      } else {
        valueToSend = answer.join(",");
      }
    }

    payload[field_name] = valueToSend;
  }
});

      // console.log("Payload to be sent:", payload);

      try {
        // console.log("Sending API request...", bodyparameters, payload);
        // TODO: terug uit comments halen en response = true weg halen.
        const response = await fetch(url, {
          method: method,
          headers: {
            "Content-Type": bodyparameters, // Typically 'application/json'
          },
          body: JSON.stringify(payload),
        });
        // console.log("API response status:", response.status);

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const text = await response.text();
        let data;
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (error) {
            console.error("Error parsing JSON:", error);
            data = {};
          }
        } else {
          data = {}; // Of een andere default waarde
        }
        // console.log("API Response Data:", data);

        // const data = await response.json();
        // console.log("API Response Data:", data);
        setApiResponse(data);
      } catch (error) {
        // console.error("Error sending API request:", error);
        setApiError(error.message);
      } finally {
        // console.log("API request process completed.");
        setHasSent(true);
      }
    };

    // Check if all dynamic fields have been answered
    // if (allDynamicFieldsAnswered()) {
    sendApiRequest();
    // } else {
    //   console.warn(
    //     "Not all dynamic fields have been answered. API request will not be sent."
    //   );
    // }
  }, [result, answers, hasSent]);

  return (
    <ResultScreen
      style={{ marginTop: "10px" }}
      bgColor={result.theme_color}
      bgVisible={overViewPage}
      id={result.results_item_id}
    >
      <QuestionPublicLink
        overViewPage={overViewPage}
        type="result"
        quiz_item_id={result.results_item_id}
      />
      {/* <ResultScore
        score={false}
        scoreVisible={false}
        endTotalScore={false}
        startCalculation={true}
      /> */}
      {/* <QuizResultFlexContainer>
        

        {sortedQuestions.map((question, index) => {
          if (!question) {
            console.warn(`Question at index ${index} is undefined.`);
            return null;
          }
          const answer = answers[question.quiz_item_id] || [];
          const attempt = attempts[question.quiz_item_id] || 0;
          // console.log(`Rendering Question ${index + 1}:`, question);
          // console.log(`Answer for '${question.quiz_item_id}':`, answer);
          // console.log(`Attempt for '${question.quiz_item_id}':`, attempt);
          return (
            <SimpleResultWebpageItem
              bgColor={result.theme_color}
              txtColor={result.theme_text_color}
              overViewPage={overViewPage}
              key={index}
              question={question}
              answer={answer}
              attempt={attempt}
            />
          );
        })}
      </QuizResultFlexContainer> */}

      {/* Optioneel: Toon server reactie of foutmelding */}
      {apiResponse && (
        <>
          {result.succesfull_sending_text &&
          result.succesfull_sending_text.trim() !== "" ? (
            <div style={{ marginTop: "20px", color: "#3ecf6e" }}>
              <ReactMarkdown>{result.succesfull_sending_text}</ReactMarkdown>
            </div>
          ) : (
            (() => {
              // console.log(
              //   "Standaard bericht: Uw gegevens zijn succesvol verzonden."
              // );
              return null;
            })()
          )}
        </>
      )}
      {apiError && (
        <>
          {result.unsuccesfull_sending_text &&
          result.unsuccesfull_sending_text.trim() !== "" ? (
            <div style={{ marginTop: "20px", color: "#ff4944" }}>
              <ReactMarkdown>{result.unsuccesfull_sending_text}</ReactMarkdown>
            </div>
          ) : (
            (() => {
              // console.log(
              //   "Standaard foutmelding: Er is iets misgegaan bij het verzenden van uw gegevens."
              // );
              return null;
            })()
          )}
        </>
      )}
    </ResultScreen>
  );
};

export default SendToAPI;
